import ELK from 'elkjs/lib/elk.bundled.js'
import type { Node, Edge } from '@xyflow/react'
import type { GraphNodeDto, GraphEdgeDto } from '../hooks/useGraph'

const elk = new ELK()

const NODE_WIDTH = 190
const NODE_HEIGHT = 64

export interface C4NodeData extends Record<string, unknown> {
  label: string // human displayName
  rawId: string // the precise id/address, retained for identity
  kind: string
  technology: string | undefined
  category: string
  health: string
  hasChildren: boolean
}

/**
 * Lays out the graph hierarchically (ELK 'layered', top-down) and returns React
 * Flow nodes/edges with computed positions. Edges to nodes outside the set are
 * dropped so ELK doesn't choke.
 */
export async function elkLayout(
  nodes: GraphNodeDto[],
  edges: GraphEdgeDto[],
): Promise<{ nodes: Node<C4NodeData>[]; edges: Edge[] }> {
  const keys = new Set(nodes.map((n) => n.key))
  const safeEdges = edges.filter((e) => keys.has(e.from) && keys.has(e.to))

  const graph = {
    id: 'root',
    layoutOptions: {
      'elk.algorithm': 'layered',
      'elk.direction': 'DOWN',
      'elk.spacing.nodeNode': '40',
      'elk.layered.spacing.nodeNodeBetweenLayers': '60',
    },
    children: nodes.map((n) => ({ id: n.key, width: NODE_WIDTH, height: NODE_HEIGHT })),
    edges: safeEdges.map((e, i) => ({ id: `e${i}`, sources: [e.from], targets: [e.to] })),
  }

  const laid = await elk.layout(graph)
  const positioned: Node<C4NodeData>[] = nodes.map((n) => {
    const child = laid.children?.find((c) => c.id === n.key)
    return {
      id: n.key,
      type: 'c4',
      position: { x: child?.x ?? 0, y: child?.y ?? 0 },
      data: {
        label: n.displayName,
        rawId: n.name,
        kind: n.kind,
        technology: n.technology ?? undefined,
        category: n.category,
        health: n.health,
        hasChildren: n.hasChildren ?? false,
      },
    }
  })

  const rfEdges: Edge[] = safeEdges.map((e, i) => ({
    id: `e${i}`,
    source: e.from,
    target: e.to,
    label: e.description ?? undefined,
    animated: false,
  }))

  return { nodes: positioned, edges: rfEdges }
}
